import { useMemo } from "react";
import { BookOpen, Sparkles, ArrowRight, Quote, Languages, Mic } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/common/UserAvatar";
import { useDailyTopic } from "@/hooks/use-daily-topic";
import { slateCardClass } from "@/lib/card-styles";
import { getCategoryMeta } from "@/lib/categories";
import { typeMetaClass, typeTitleClass } from "@/lib/typography";
import { cn } from "@/lib/utils";

export interface TopicOfTheDayFallback {
  id: string;
  topic: string;
  slug: string | null;
  banner_url?: string | null;
  category_name?: string | null;
  question_count?: number;
  word_count?: number;
  creator_display_name?: string | null;
  creator_avatar_url?: string | null;
}

interface TopicOfTheDayProps {
  /** Used only when the get_topic_of_day RPC yields nothing. */
  fallback?: TopicOfTheDayFallback | null;
  onPractice: (slug: string, title: string) => void;
  onView: (slug: string, title: string) => void;
}

/** Stable per-UTC-day offset, so every visitor gets the same pick. */
const daySeed = (): number => {
  const now = new Date();
  return Math.floor(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) / 86400000);
};

const TopicOfTheDay = ({ fallback, onPractice, onView }: TopicOfTheDayProps) => {
  const { t } = useTranslation();
  const { data: rpcTopic, isLoading } = useDailyTopic();

  const topic = useMemo(() => {
    if (rpcTopic) {
      return {
        id: rpcTopic.id,
        title: rpcTopic.topic,
        slug: rpcTopic.slug,
        banner: rpcTopic.banner_url,
        category: rpcTopic.category_name,
        questionCount: Array.isArray(rpcTopic.questions) ? rpcTopic.questions.length : 0,
        wordCount: Array.isArray(rpcTopic.vocabulary) ? rpcTopic.vocabulary.length : 0,
        creatorName: rpcTopic.creator_display_name,
        creatorAvatar: rpcTopic.creator_avatar_url,
        creatorId: rpcTopic.user_id,
      };
    }
    if (fallback) {
      return {
        id: fallback.id,
        title: fallback.topic,
        slug: fallback.slug,
        banner: fallback.banner_url ?? null,
        category: fallback.category_name ?? null,
        questionCount: fallback.question_count ?? 0,
        wordCount: fallback.word_count ?? 0,
        creatorName: fallback.creator_display_name ?? null,
        creatorAvatar: fallback.creator_avatar_url ?? null,
        creatorId: undefined,
      };
    }
    return null;
  }, [rpcTopic, fallback]);

  if (isLoading && !topic) return null;
  if (!topic) return null;

  const categoryName = topic.category || "General";
  const category = getCategoryMeta(categoryName);
  const CategoryIcon = category.icon ?? Sparkles;
  const bannerSrc = topic.banner || category.bannerUrl;

  return (
    <section aria-labelledby="topic-of-the-day-heading">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2
          id="topic-of-the-day-heading"
          className={cn(typeTitleClass, "inline-flex items-center gap-2")}
        >
          <Quote className="h-4 w-4 text-secondary" aria-hidden="true" />
          {t("home.topicOfDay.title", "Topic of the day")}
        </h2>
        {/* The card already carries a "Curated daily" pill; on a phone the
            header has no room for both and this one clipped at the edge. */}
        <span className={cn(typeMetaClass, "hidden sm:inline")}>
          {t("home.topicOfDay.curatedDaily", "Curated daily")}
        </span>
      </div>

      <article
        className={cn(
          slateCardClass,
          "group relative overflow-hidden",
          // Warm editorial lesson sheet with subtle amber wash
          "bg-[linear-gradient(120deg,hsl(38_60%_97.5%)_0%,hsl(var(--background))_45%,hsl(38_45%_98%)_100%)]",
          "dark:bg-[linear-gradient(120deg,hsl(30_14%_14%)_0%,hsl(var(--background))_45%,hsl(30_12%_13%)_100%)]",
          "transition-all duration-200 hover:border-primary/35 hover:shadow-md"
        )}
      >
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-1 bg-secondary"
        />

        <div className="flex flex-col gap-4 p-4 pl-5 sm:flex-row sm:items-center sm:gap-5 sm:p-5 sm:pl-6">
          {/* Always show a rich visual cover (topic banner or category cover) */}
          <div
            onClick={() => {
              if (topic.slug) onView(topic.slug, topic.title);
            }}
            className="relative h-32 w-full shrink-0 cursor-pointer overflow-hidden rounded-xl border border-stone-200/70 sm:h-28 sm:w-44 dark:border-stone-800"
          >
            <img
              src={bannerSrc}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-stone-950/60 via-stone-950/10 to-transparent" />
            <span
              className={cn(
                "absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-md px-2 py-0.5",
                "border border-white/20 bg-stone-900/75 text-[11px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm"
              )}
            >
              <CategoryIcon className="h-3 w-3 text-amber-300" aria-hidden="true" />
              {t(`pages.topics.categories.${categoryName}`, { defaultValue: categoryName })}
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/70 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:border-amber-800/60 dark:bg-amber-500/15 dark:text-amber-300">
                <Sparkles className="h-2.5 w-2.5" aria-hidden="true" />
                {t("home.topicOfDay.curatedDaily", "Curated daily")}
              </span>
            </div>

            <h3
              onClick={() => {
                if (topic.slug) onView(topic.slug, topic.title);
              }}
              className="cursor-pointer font-display text-xl font-semibold leading-tight tracking-tight text-stone-900 transition-colors group-hover:text-primary sm:text-2xl dark:text-stone-50"
            >
              {topic.title}
            </h3>

            {/* Educational curriculum pills + author */}
            <div className="mt-2.5 flex flex-wrap items-center gap-2 text-xs text-stone-600 dark:text-stone-300">
              {topic.questionCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full border border-stone-200/80 bg-white/80 px-2.5 py-0.5 text-[11px] font-medium dark:border-stone-800 dark:bg-stone-900/70">
                  <BookOpen className="h-3 w-3 text-primary" aria-hidden="true" />
                  {t("common.count.question", { count: topic.questionCount })}
                </span>
              )}
              {topic.wordCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full border border-stone-200/80 bg-white/80 px-2.5 py-0.5 text-[11px] font-medium dark:border-stone-800 dark:bg-stone-900/70">
                  <Languages className="h-3 w-3 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                  {t("common.count.vocab", { count: topic.wordCount })}
                </span>
              )}
              {topic.questionCount === 0 && topic.wordCount === 0 && (
                <span className="inline-flex items-center gap-1 rounded-full border border-stone-200/80 bg-white/80 px-2.5 py-0.5 text-[11px] font-medium dark:border-stone-800 dark:bg-stone-900/70">
                  <Mic className="h-3 w-3 text-primary" aria-hidden="true" />
                  {t("home.topicOfDay.openEnded", "Open-ended conversation")}
                </span>
              )}
              {topic.creatorName && (
                <span className="inline-flex min-w-0 items-center gap-1.5 pl-1 text-stone-500 dark:text-stone-400">
                  <UserAvatar
                    userId={topic.creatorId ?? null}
                    displayName={topic.creatorName}
                    avatarUrl={topic.creatorAvatar}
                    size="xs"
                    className="shrink-0"
                  />
                  <span className="truncate">{topic.creatorName}</span>
                </span>
              )}
            </div>
          </div>

          {/* Flat bg-primary CTA (no gradient) so TodayStrip remains the only
              primaryCtaClass gradient button on the page. */}
          <div className="flex shrink-0 items-center gap-2">
            <Button
              size="sm"
              onClick={() => {
                if (topic.slug) onPractice(topic.slug, topic.title);
              }}
              className="h-9 gap-1.5 rounded-full bg-primary-strong px-4 text-xs font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
            >
              <Mic className="h-3.5 w-3.5" aria-hidden="true" />
              {t("home.topicOfDay.practise", "Practise this")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (topic.slug) onView(topic.slug, topic.title);
              }}
              className="h-9 gap-1.5 rounded-full px-3 text-xs"
            >
              {t("home.topicOfDay.view", "View")}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </article>
    </section>
  );
};

export { TopicOfTheDay };
export default TopicOfTheDay;
export { daySeed };
